import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import type { Event } from '@/types/calendar';
import type { Category } from '@/types/calendar';
import { ACADEMIC_MONTHS } from '@calendar/lib/constants';
import { getSeason as getSeasonForDate } from '@calendar/lib/season';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Parse a date string (YYYY-MM-DD) in local timezone
 * Avoids UTC conversion issues where new Date("2025-10-24") becomes Oct 23 in PDT
 */
export function parseDateString(dateString: string): Date {
  const [ year, month, day ] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
}

/**
 * Parse a datetime string (YYYY-MM-DDTHH:mm:ss) in local timezone
 * Used for sorting events by both date and time
 * Returns null if datetime string is invalid or undefined
 */
export function parseDateTimeString(datetimeString?: string): Date | null {
  if (!datetimeString) {
    return null;
  }

  const [ datePart, timePart ] = datetimeString.split('T');
  if (!datePart || !timePart) {
    return null;
  }

  const [ year, month, day ] = datePart.split('-').map(Number);
  const [ hours, minutes, seconds ] = timePart.split(':').map(Number);
  return new Date(year, month - 1, day, hours, minutes, seconds);
}

export function formatDate(date: Date) {
  return {
    fullDate: date.toLocaleDateString('en-US'),
    dayName: date.toLocaleString('en-US', { weekday: 'short' }),
    monthName: date.toLocaleString('en-US', { month: 'short' }),
    dayNumber: date.getDate(),
  };
}

export function formatFeatureDate(date: string, time: string) {
  const dateObj = parseDateString(date);

  // Get day of week (short form)
  const dayOfWeek = dateObj.toLocaleDateString('en-US', { weekday: 'short' });

  // Get month (short form)
  const month = dateObj.toLocaleDateString('en-US', { month: 'short' });

  // Get day of month
  const dayOfMonth = dateObj.getDate();

  // Use the provided time string directly (already formatted as "7:30 p.m.")
  // Convert to uppercase P.M./A.M. format to match existing pattern
  const formattedTime = time.replace('p.m.', 'P.M.').replace('a.m.', 'A.M.');

  // Combine all parts
  return `${dayOfWeek} — ${month} ${dayOfMonth} @ ${formattedTime}`;
}

export function getFullMonthFromShort(shortMonth: string) {
  // Create a mapping for the months
  const monthMap = {
    'JAN': 0, 'FEB': 1, 'MAR': 2, 'APR': 3, 'MAY': 4, 'JUN': 5,
    'JUL': 6, 'AUG': 7, 'SEP': 8, 'OCT': 9, 'NOV': 10, 'DEC': 11
  };

  const monthIndex = monthMap[shortMonth.toUpperCase() as keyof typeof monthMap];

  if (monthIndex === undefined) {
    return null;
  }

  // Create a date object with any year/day, we only care about the month
  const date = new Date(2000, monthIndex, 1);

  // Get the full month name
  return date.toLocaleString('default', { month: 'long' });
}

export function getSeason(date: Date | string) {
  return getSeasonForDate(date);
}

/**
 * Display event categories with hierarchy.
 * Format: "Primary - Parent, Other Top-Level Categories"
 *
 * @param categories - Array of category slugs assigned to the event
 * @param allCategories - All available categories with parent/child structure
 * @param primaryCategory - The primary category slug (from Yoast)
 */
export function displayCategories(
  categories: string[],
  allCategories: Category[],
  primaryCategory?: string
) {
  if (!categories.length) return '';

  // Find the primary category (use provided or fall back to first)
  const primarySlug = primaryCategory || categories[0];

  // Helper to find a category (could be parent or child)
  const findCategory = (slug: string): { name: string; parentId?: string; parentName?: string } | null => {
    // Check if it's a parent category
    const parentCat = allCategories.find((c) => c.id === slug);
    if (parentCat) {
      return { name: parentCat.name };
    }

    // Check if it's a child category
    for (const parent of allCategories) {
      const child = parent.children?.find((c) => c.id === slug);
      if (child) {
        return { name: child.name, parentId: parent.id, parentName: parent.name };
      }
    }

    return null;
  };

  const parts: string[] = [];
  const shownSlugs = new Set<string>();

  // Build primary category display (with parent if exists)
  const primaryInfo = findCategory(primarySlug);
  if (primaryInfo) {
    if (primaryInfo.parentName) {
      // Format: "Child - Parent"
      parts.push(`${primaryInfo.name} - ${primaryInfo.parentName}`);
      shownSlugs.add(primarySlug);
      if (primaryInfo.parentId) {
        shownSlugs.add(primaryInfo.parentId);
      }
    } else {
      parts.push(primaryInfo.name);
      shownSlugs.add(primarySlug);
    }
  }

  // Add all other top-level categories that aren't already shown
  for (const categorySlug of categories) {
    if (shownSlugs.has(categorySlug)) continue;

    // Only add if it's a top-level category
    const parentCat = allCategories.find((c) => c.id === categorySlug);
    if (parentCat) {
      parts.push(parentCat.name);
      shownSlugs.add(categorySlug);
    }
  }

  return parts.join(', ');
}

export function getMonthYear(monthIndex: number, season: string) {
  const parts = season.split('-');
  const year = monthIndex < 5 ? parts[0] : `20${parts[1]}`;
  return year;
}

export function getMonthIndex(month: string): number | null {
  return ACADEMIC_MONTHS.findIndex((m) => m.id === month);
}

export function groupEventsByDate(events: Event[]) {
  return events.reduce(
    (groups, event) => {
      const eventDate = parseDateString(event.date);
      const dates = formatDate(eventDate);
      const date = `${dates.monthName}-${dates.dayName}-${dates.dayNumber}-${dates.fullDate}`;
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(event);
      return groups;
    },
    {} as Record<string, Event[]>,
  );
}

export function rotateMonths() {
  const [ year, month, day ] = window.curtisCalendar.seasonStart.split('-').map(Number);
  const date = new Date(year, month - 1, day); // Creates local time date
  const localeMonth = date.toLocaleString('default', { month: 'short' }).toLowerCase();
  const startId = localeMonth.slice(0, 3);
  const startIndex = ACADEMIC_MONTHS.findIndex(m => m.id === startId);
  if (startIndex === -1) return ACADEMIC_MONTHS; // fallback

  return [
    ...ACADEMIC_MONTHS.slice(startIndex),
    ...ACADEMIC_MONTHS.slice(0, startIndex)
  ];
}
